import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { getConsent, setConsent, hasConsentChoice } from '../utils/gdprConsent';

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    setVisible(!hasConsentChoice());
    const open = () => {
      const c = getConsent();
      setAnalytics(c.analytics);
      setCustomizeOpen(false);
      setVisible(true);
    };
    window.addEventListener('cookie-preferences-requested', open);
    return () => window.removeEventListener('cookie-preferences-requested', open);
  }, []);

  const hide = () => {
    setVisible(false);
    setCustomizeOpen(false);
  };

  const acceptAll = () => {
    setConsent({ essential: true, analytics: true });
    hide();
  };

  const rejectOptional = () => {
    setConsent({ essential: true, analytics: false });
    hide();
  };

  const saveCustomize = () => {
    setConsent({ essential: true, analytics });
    hide();
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto rounded-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl overflow-hidden">
          <div className="p-4 md:p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                <Cookie className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white mb-1">Cookie &amp; local storage</h3>
                <p className="text-sm text-white/60 mb-4">
                  We use cookies, localStorage and IndexedDB so the Virtual Try-On works (e.g. preferences, selfies, clips).
                  Optional analytics help us improve the product. You can accept all, reject optional, or customize.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="px-4 py-2 rounded-xl bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors"
                  >
                    Accept all
                  </button>
                  <button
                    type="button"
                    onClick={rejectOptional}
                    className="px-4 py-2 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/15 transition-colors"
                  >
                    Reject optional
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomizeOpen(!customizeOpen)}
                    className="px-4 py-2 rounded-xl bg-white/5 text-white/70 text-sm font-medium hover:bg-white/10 hover:text-white transition-colors inline-flex items-center gap-1"
                  >
                    Customize
                    {customizeOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>

                <AnimatePresence>
                  {customizeOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-4 overflow-hidden"
                    >
                      <div className="pt-4 border-t border-white/10 space-y-3">
                        <label className="flex items-center justify-between gap-4 cursor-pointer">
                          <span className="text-sm text-white/70">Essential (cookies, localStorage, IndexedDB for VTO)</span>
                          <span className="text-xs text-white/40">Always on</span>
                        </label>
                        <label className="flex items-center justify-between gap-4 cursor-pointer group">
                          <span className="text-sm text-white/70 group-hover:text-white transition-colors">Analytics (usage stats)</span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={analytics}
                            onClick={() => setAnalytics((s) => !s)}
                            className={`relative w-11 h-6 rounded-full transition-colors ${analytics ? 'bg-[#3B82F6]' : 'bg-white/20'}`}
                          >
                            <span
                              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${analytics ? 'left-6' : 'left-1'}`}
                            />
                          </button>
                        </label>
                        <div className="flex items-center gap-2 pt-2">
                          <button
                            type="button"
                            onClick={saveCustomize}
                            className="px-4 py-2 rounded-xl bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] transition-colors"
                          >
                            Save preferences
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <a href="/privacy#cookies" className="text-sm text-[#3B82F6] hover:underline">
                  Privacy &amp; cookies
                </a>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('cookie-preferences-requested'))}
                  className="text-xs text-white/50 hover:text-white/80 hover:underline text-left"
                >
                  Cookie preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
